<script lang="ts">
  import { installImpersonationInterceptor, reloadIdentity } from "./lib/utils/impersonation";
  import "./tailwind.css"
  import Login from "./Login.svelte";
  import Dashboard from "./Dashboard.svelte";
  import Router, { push } from "svelte-spa-router";
  import wrap from "svelte-spa-router/wrap";
  import Layout from "./lib/components/Layout.svelte";
  import { onMount } from "svelte";
  import { clearUser, fetchUserProfile, user } from "./lib/stores/user";
  import { telemetry } from './lib/stores/telemetry';
  import { installSessionExpiryInterceptor, rememberExpiredSession } from './lib/utils/sessionExpiry';

  import PasswordRecovery from "./PasswordRecovery.svelte";
  import CredentialSetup from "./CredentialSetup.svelte";
  import Forbidden from './Forbidden.svelte';
  import NotFound from './NotFound.svelte';
  import Profile from './Profile.svelte';
  import Settings from './Settings.svelte';
  import PublicInvoicePay from './PublicInvoicePay.svelte';
  import { canAccessRoute } from './lib/utils/navigation';
  import NetworkStatusIndicator from './lib/components/NetworkStatusIndicator.svelte';

  const lazy = (fn: () => Promise<any>) => wrap({ asyncComponent: fn });

  const appRoutes = {
    "/pay/:token": PublicInvoicePay,
    "/installer/jobs": lazy(() => import("./InstallerJobs.svelte")),
    "/profile": Profile,
    "/settings": Settings,
    "/copilot": lazy(() => import("./Copilot.svelte")),
    "/chat": lazy(() => import("./Copilot.svelte")),
    "/devices/:id": lazy(() => import("./DeviceDetail.svelte")),
    "/devices": lazy(() => import("./Devices.svelte")),
    "/properties/:id": lazy(() => import("./AdminProperty.svelte")),
    "/properties": lazy(() => import("./ManagerProperties.svelte")),
    "/alerts": lazy(() => import("./AdminAlerts.svelte")),
    "/admin": lazy(() => import("./SystemAdmin.svelte")),
    "/admin/workflows": lazy(() => import("./AdminWorkflows.svelte")),
    "/admin/customers/:id": lazy(() => import("./AdminCustomerAccounts.svelte")),
    "/admin/customers": lazy(() => import("./AdminCustomers.svelte")),
    "/admin/organizations": lazy(() => import("./AdminCompanies.svelte")),
    "/admin/companies": lazy(() => import("./AdminCompanies.svelte")),
    "/admin/users": lazy(() => import("./AdminUsers.svelte")),
    "/admin/devices/:id": lazy(() => import("./DeviceDetail.svelte")),
    "/admin/devices": lazy(() => import("./Devices.svelte")),
    "/admin/properties/:id": lazy(() => import("./AdminProperty.svelte")),
    "/admin/properties": lazy(() => import("./AdminProperties.svelte")),
    "/admin/alerts": lazy(() => import("./AdminAlerts.svelte")),
    "/": Dashboard,
    "*": NotFound,
  };

  const loginRoutes = {
    "/login": Login,
    "*": Login,
  };

  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/pay/')) {
    const token = window.location.pathname.slice('/pay/'.length);
    window.location.replace(`${window.location.origin}/#/pay/${token}`);
  }
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/wg-port')) {
    window.location.replace(`${window.location.origin}/wg-port/`);
  }

  let isLoggedIn = $derived($user !== null);
  let checkingAuth = $state(true);
  let currentHash = $state(window.location.hash);

  async function checkAuth() {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/wg-port')) {
      checkingAuth = false;
      return;
    }
    if (['#/password/setup','#/password/recovery'].includes(window.location.hash.split('?')[0]) || window.location.hash.startsWith('#/pay/')) { checkingAuth = false; return; }
    if (window.location.hash.startsWith('#/login') && !localStorage.getItem('auth_hint')) {
      checkingAuth = false;
      return;
    }
    try {
      const ok = await fetchUserProfile();
      if (!ok && window.location.hash !== '#/login') {
        push('/login');
      }
    } catch (e) {
    } finally {
      checkingAuth = false;
    }
  }

  onMount(() => {
    const restoreFetch = installSessionExpiryInterceptor(() => {
      if (!isLoggedIn) return;
      rememberExpiredSession(window.location.hash);
      telemetry.disconnect();
      clearUser();
      push('/login');
    });
    const restoreImpersonation = installImpersonationInterceptor(() => {
      telemetry.disconnect();
      reloadIdentity('/admin/users');
    });
    void checkAuth();
    return () => { restoreImpersonation(); restoreFetch(); };
  });

  function onHashChange() {
    currentHash = window.location.hash;
    if (currentHash !== '#/login' && !isLoggedIn && !checkingAuth && !currentHash.startsWith('#/pay/')) {
      checkAuth();
    }
  }

  const isLoginPage = $derived(currentHash === '#/login' || !isLoggedIn);
</script>

<svelte:window onhashchange={onHashChange} />

{#if checkingAuth}
  <div class="min-h-screen flex items-center justify-center bg-base-100">
    <div class="loading loading-spinner loading-lg text-primary"></div>
  </div>
{:else if currentHash.split('?')[0] === '#/password/recovery'}
  <PasswordRecovery/>
{:else if currentHash.split('?')[0] === '#/password/setup'}
  <CredentialSetup/>
{:else if currentHash.startsWith('#/pay/')}
  <div class="min-h-screen font-sans bg-base-200">
    <Router routes={{ "/pay/:token": PublicInvoicePay }}/>
  </div>
{:else if !isLoggedIn}
  <div class="min-h-screen font-sans bg-base-100">
    <Router routes={loginRoutes}/>
  </div>
{:else}
  <Layout>
    {#if canAccessRoute(currentHash, $user?.user_type)}
      <Router routes={appRoutes}/>
    {:else}
      <Forbidden />
    {/if}
  </Layout>
{/if}

<NetworkStatusIndicator />

